/**
 * FamillePiecesPanel — bibliothèque de pièces d'une famille (réutilisable).
 *
 * Affiche, pour un module donné, la liste des natures de pièces (communes +
 * spécifiques du module) en indiquant pour chacune si la famille l'a DÉJÀ fournie.
 * Une pièce déposée une fois sert tous les dossiers ; on ne redemande que ce qui
 * manque. Le dépôt remplace l'ancienne version (mise à jour à tout moment).
 *
 * Deux usages :
 *   - parent (espace famille)  : dépose / met à jour ses pièces, télécharge.
 *   - staff (fiche famille/GRC) : idem + marque la conformité (canValidate).
 */

import { useEffect, useState, useCallback } from 'react';
import { Card, Button, Badge } from '@/components/ui';
import type { Module } from '@/components/ui';
import { api } from '@/lib/api';
import type { PieceType, FamillePiece } from '@/lib/api';
import { FileText, Upload, Download, Check, X, Clock } from 'lucide-react';

type AccentModule = Extract<Module, 'cantine' | 'petite_enfance'> | 'grc';

const ACCENT_BTN: Record<AccentModule, 'cantine' | 'petite_enfance' | 'grc'> = {
  cantine: 'cantine',
  petite_enfance: 'petite_enfance',
  grc: 'grc',
};

interface Ligne {
  type: PieceType;
  piece: FamillePiece | null;
}

export function FamillePiecesPanel({
  familleId,
  module,
  accent = 'petite_enfance',
  canValidate = false,
  setError,
}: {
  familleId: string;
  /** Filtre les natures spécifiques (ex. 'petite_enfance' inclut le médical PE). */
  module?: string;
  accent?: AccentModule;
  /** Staff : peut marquer conforme / non conforme. */
  canValidate?: boolean;
  setError: (s: string | null) => void;
}) {
  const [types, setTypes] = useState<PieceType[]>([]);
  const [pieces, setPieces] = useState<FamillePiece[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [t, p] = await Promise.all([
        api.famillePieces.types(module),
        api.famillePieces.list(familleId),
      ]);
      setTypes(t.pieceTypes);
      setPieces(p.pieces);
    } catch {
      setError('Impossible de charger les pièces de la famille.');
    } finally {
      setLoading(false);
    }
  }, [familleId, module, setError]);

  useEffect(() => { void load(); }, [load]);

  const lignes: Ligne[] = types.map((type) => ({
    type,
    piece: pieces.find((p) => p.pieceTypeId === type.id) ?? null,
  }));
  const fournies = lignes.filter((l) => l.piece).length;
  const manquantes = lignes.filter((l) => !l.piece && l.type.portee === 'commun').length;

  const deposer = async (pieceTypeId: string, file: File) => {
    setBusy(pieceTypeId); setError(null);
    try {
      await api.famillePieces.deposer(familleId, pieceTypeId, file);
      await load();
    } catch {
      setError('Le dépôt a échoué (formats acceptés : PDF, JPG, PNG).');
    } finally { setBusy(null); }
  };

  const telecharger = async (piece: FamillePiece) => {
    try {
      const { url } = await api.famillePieces.url(familleId, piece.id);
      window.open(url, '_blank', 'noopener');
    } catch { setError('Téléchargement impossible.'); }
  };

  const cocher = async (piece: FamillePiece, conforme: boolean) => {
    setBusy(piece.id); setError(null);
    try {
      await api.famillePieces.cocher(familleId, piece.id, conforme);
      await load();
    } catch { setError('Mise à jour impossible.'); }
    finally { setBusy(null); }
  };

  if (loading) {
    return <Card variant="glass" padded><div className="py-8 text-center text-sm text-ink-subtle">Chargement…</div></Card>;
  }

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2 text-xs text-ink-muted">
        <Badge variant="success" size="sm">{fournies} fournie{fournies > 1 ? 's' : ''}</Badge>
        {manquantes > 0 && <Badge variant="warning" size="sm">{manquantes} manquante{manquantes > 1 ? 's' : ''}</Badge>}
        <span className="text-ink-subtle">Une pièce déposée sert tous les dossiers de la famille.</span>
      </div>

      <div className="space-y-2">
        {lignes.map(({ type, piece }) => (
          <Card key={type.id} variant="glass" className="p-3">
            <div className="flex items-center gap-3">
              <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-r-lg bg-surface/60">
                <FileText className="h-4.5 w-4.5 text-ink-muted" aria-hidden="true" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span className="font-medium text-ink">{type.libelle}</span>
                  {type.portee === 'specifique' && <Badge variant="neutral" size="sm">spécifique</Badge>}
                </div>
                {piece ? (
                  <div className="text-[11px] text-ink-subtle">
                    {piece.nomFichier} · v{piece.version}
                    {piece.conforme === true && ' · conforme'}
                    {piece.conforme === false && ' · non conforme'}
                    {piece.conforme == null && ' · en attente de vérification'}
                  </div>
                ) : (
                  <div className="text-[11px] text-ink-subtle">Non fournie</div>
                )}
              </div>

              <div className="flex flex-shrink-0 flex-wrap items-center gap-1.5">
                {piece ? (
                  <>
                    {piece.conforme === true
                      ? <Badge variant="success" size="sm"><Check className="mr-0.5 h-3 w-3" aria-hidden="true" />OK</Badge>
                      : piece.conforme === false
                        ? <Badge variant="urgent" size="sm"><X className="mr-0.5 h-3 w-3" aria-hidden="true" />Refusée</Badge>
                        : <Badge variant="warning" size="sm"><Clock className="mr-0.5 h-3 w-3" aria-hidden="true" />À vérifier</Badge>}
                    <Button type="button" variant="ghost" size="sm" onClick={() => void telecharger(piece)}>
                      <Download className="h-3.5 w-3.5" aria-hidden="true" />
                    </Button>
                    {canValidate && (
                      <>
                        <Button type="button" variant="ghost" size="sm" disabled={busy === piece.id}
                          onClick={() => void cocher(piece, true)} aria-label="Marquer conforme">
                          <Check className="h-3.5 w-3.5 text-success" aria-hidden="true" />
                        </Button>
                        <Button type="button" variant="ghost" size="sm" disabled={busy === piece.id}
                          onClick={() => void cocher(piece, false)} aria-label="Marquer non conforme">
                          <X className="h-3.5 w-3.5 text-danger" aria-hidden="true" />
                        </Button>
                      </>
                    )}
                    <FileInput accent={accent} label="Remplacer" busy={busy === type.id}
                      onPick={(f) => void deposer(type.id, f)} />
                  </>
                ) : (
                  <FileInput accent={accent} label="Déposer" busy={busy === type.id}
                    onPick={(f) => void deposer(type.id, f)} primary />
                )}
              </div>
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
}

/** Petit bouton d'upload qui déclenche un <input type=file> caché. */
function FileInput({
  label, accent, busy, primary, onPick,
}: {
  label: string;
  accent: AccentModule;
  busy: boolean;
  primary?: boolean;
  onPick: (file: File) => void;
}) {
  const id = `fi-${Math.random().toString(36).slice(2, 9)}`;
  return (
    <label htmlFor={id}>
      <input
        id={id} type="file" accept=".pdf,.jpg,.jpeg,.png,.webp" className="sr-only"
        onChange={(e) => { const f = e.target.files?.[0]; if (f) onPick(f); e.currentTarget.value = ''; }}
      />
      <Button type="button" variant={primary ? ACCENT_BTN[accent] : 'secondary'} size="sm" disabled={busy}
        onClick={(e) => { e.preventDefault(); (e.currentTarget.previousElementSibling as HTMLInputElement)?.click(); }}>
        <Upload className="h-3.5 w-3.5" aria-hidden="true" /> {busy ? '…' : label}
      </Button>
    </label>
  );
}
